// Receipts: read-only payment proof. Each receipt composes a recorded
// payment with its invoice and the posted payment journal entry —
// rendering posts nothing and consumes no sequence number, so re-rendering
// always yields the same receipt.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { StatusDot } from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import { InvoicePicker } from "@/components/Pickers";
import { listPayments, type InvoicePayment } from "@/lib/api";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

export function ReceiptsPage() {
  const [receipts, setReceipts] = useState<InvoicePayment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invoiceFilter, setInvoiceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const reload = () => {
    setError(null);
    listPayments({
      ...(invoiceFilter ? { invoice_id: invoiceFilter } : {}),
      ...(statusFilter === "unreconciled" || statusFilter === "reconciled"
        ? { status: statusFilter }
        : {}),
    })
      .then(setReceipts)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  };

  useEffect(reload, [invoiceFilter, statusFilter]);

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Receipts"
        description="One record for each customer cash receipt, with any allocated and unapplied AR credit."
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          reload();
        }}
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <div className="grid min-w-52 flex-1 gap-1 text-xs font-medium">
          <label htmlFor="receipt-invoice">Invoice</label>
          <InvoicePicker
            id="receipt-invoice"
            value={invoiceFilter}
            onChange={setInvoiceFilter}
          />
        </div>
        <label
          htmlFor="receipt-status"
          className="grid gap-1 text-xs font-medium"
        >
          Status
          <Select
            id="receipt-status"
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="All"
            options={[
              { value: "unreconciled", label: "Unreconciled" },
              { value: "reconciled", label: "Reconciled" },
            ]}
          />
        </label>
        <Button type="submit" variant="secondary">
          Refresh
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            setInvoiceFilter("");
            setStatusFilter("");
          }}
        >
          Clear
        </Button>
      </form>

      {receipts === null ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : receipts.length === 0 ? (
        invoiceFilter || statusFilter ? (
          <div className="surface rounded-xl border border-[var(--border)] p-4 text-sm">
            No receipts match these filters.
            <div className="mt-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  setInvoiceFilter("");
                  setStatusFilter("");
                }}
              >
                Clear filters
              </Button>
            </div>
          </div>
        ) : (
          <EmptyState
            title="No receipts yet"
            reason="Receipts appear here when customer cash is recorded, whether it is applied to invoices now or later."
            action={
              <Button asChild>
                <Link to="/payments" search={(previous) => ({ ...previous })}>
                  Record a payment
                </Link>
              </Button>
            }
          />
        )
      ) : (
        <>
        <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Receipt</TableHead>
                <TableHead>Customer reference</TableHead>
                <TableHead>Received</TableHead>
                <TableHead>Reconciliation</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Unapplied</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {receipts.map((receipt) => (
                <TableRow key={receipt.id}>
                  <TableCell className="font-mono text-xs">
                    <Link
                      to="/receipts/$paymentId"
                      params={{ paymentId: receipt.id }}
                      className="hover:underline"
                    >
                      RCP-{receipt.id.slice(0, 8).toUpperCase()}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {receipt.crm_contact_id
                      ? "Linked CRM contact"
                      : receipt.invoice_id
                        ? "Invoice receipt"
                        : "Customer required"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {new Date(receipt.received_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <StatusDot
                      tone={receipt.status === "reconciled" ? "success" : "idle"}
                      label={receipt.status === "reconciled" ? "Reconciled" : "Unreconciled"}
                    />
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMoney({
                      cents: receipt.amount_cents,
                      currency: "USD",
                    })}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatMoney({
                      cents: receipt.unapplied_amount_cents,
                      currency: "USD",
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="stack-list grid gap-3 min-[64rem]:hidden">
          {receipts.map((receipt) => (
            <article
              key={receipt.id}
              className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
            >
              <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                <Link
                  to="/receipts/$paymentId"
                  params={{ paymentId: receipt.id }}
                  className="record-title underline-offset-4 hover:underline"
                >
                  RCP-{receipt.id.slice(0, 8).toUpperCase()}
                </Link>
                <span className="tabular-nums">
                  {formatMoney({
                    cents: receipt.amount_cents,
                    currency: "USD",
                  })}
                </span>
              </div>
              <CardField label="Customer reference">
                {receipt.crm_contact_id
                  ? "Linked CRM contact"
                  : receipt.invoice_id
                    ? "Invoice receipt"
                    : "Customer required"}
              </CardField>
              <CardField label="Received">
                {new Date(receipt.received_at).toLocaleDateString()}
              </CardField>
              <CardField label="Reconciliation">
                <StatusDot
                  tone={receipt.status === "reconciled" ? "success" : "idle"}
                  label={
                    receipt.status === "reconciled"
                      ? "Reconciled"
                      : "Unreconciled"
                  }
                />
              </CardField>
              <CardField label="Unapplied">
                {formatMoney({
                  cents: receipt.unapplied_amount_cents,
                  currency: "USD",
                })}
              </CardField>
              <div className="flex flex-wrap gap-2 pt-3">
                <Button asChild variant="secondary" size="sm">
                  <Link
                    to="/receipts/$paymentId"
                    params={{ paymentId: receipt.id }}
                  >
                    Open
                  </Link>
                </Button>
              </div>
            </article>
          ))}
        </div>
        </>
      )}
    </div>
  );
}
